/**
 * Client-side reporting model for the NVS reporting portal.
 *
 * Everything is real data, served by the Worker from Postgres functions over
 * the Locate2u-synced tables:
 *   /api/reports/filters  → report_filters()   (filter options + date bounds)
 *   /api/reports/summary  → report_summary(...) (DIFOT / failed / driver perf)
 *   /api/reports/users    → users table        (team directory)
 *
 * Scope note: the source has no carrier (NVS/DPD), cost, or promised-SLA-window
 * fields. So DIFOT reports Delivered + In Full (On-Time is shown as unavailable),
 * Delivery cost has no source, and Customer feedback has no ratings yet.
 */

export interface ReportTotals {
  stops: number;
  delivered: number;
  inFull: number;
  difot: number;
  failed: number;
  cancelled: number;
  inProgress: number;
  drivers: number;
  onTime: number | null;
  avgDistanceKm: number | null;
}

export interface ReportRates {
  delivered: number; // 0..1
  inFull: number;
  difot: number;
}

export interface RegionRow {
  region: string;
  stops: number;
  difotRate: number;
  deliveredRate: number;
  inFullRate: number;
  avgKm: number | null;
}

export interface DriverRow {
  driver: string;
  stops: number;
  delivered: number;
  failed: number;
  difotRate: number;
  deliveredRate: number;
  avgKm: number | null;
}

export interface DailyPoint {
  date: string;
  stops: number;
  difotRate: number;
}

export interface FailedRow {
  date: string | null;
  stopRef: string | null;
  region: string | null;
  driver: string | null;
  status: string;
  reason: string | null;
}

export interface FailedRegion {
  region: string;
  count: number;
}

export interface ReportSummary {
  from: string | null;
  to: string | null;
  totals: ReportTotals;
  rates: ReportRates;
  byRegion: RegionRow[];
  byDriver: DriverRow[];
  daily: DailyPoint[];
  failedList: FailedRow[];
  failedByRegion: FailedRegion[];
}

export interface StoreOption {
  id: number;
  name: string;
}

export interface FilterOptions {
  regions: string[];
  drivers: string[];
  stores: StoreOption[];
  dataMin: string | null;
  dataMax: string | null;
}

export interface TeamUser {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string | null;
  company: string | null;
  created_date: string | null;
}

export interface DpdReport {
  from: string | null;
  to: string | null;
  total: number;
  daily: Array<{ date: string; stops: number }>;
  weekly: Array<{ week: string; stops: number }>;
  monthly: Array<{ month: string; stops: number }>;
  byArea: Array<{ area: string; stops: number }>;
}

export interface TimingTotals {
  completedStops: number;
  withTiming: number;
  avgMinsOnSite: number | null;
  medianMinsOnSite: number | null;
  avgTransitMins: number | null;
  pctBeforeNoon: number | null; // 0..1
  avgKm: number | null;
}

export interface TimingHour {
  hour: number; // 0..23 (Europe/London)
  stops: number;
}

export interface TimingRegionRow {
  region: string;
  stops: number;
  avgMinsOnSite: number | null;
  avgKm: number | null;
}

export interface TimingDriverRow {
  driver: string;
  stops: number;
  avgMinsOnSite: number | null;
  avgKm: number | null;
}

export interface TimingReport {
  from: string | null;
  to: string | null;
  totals: TimingTotals;
  byHour: TimingHour[];
  byRegion: TimingRegionRow[];
  byDriver: TimingDriverRow[];
}

export interface MilestoneTotals {
  completedStops: number;
  /** Completed stops with at least one usable milestone position. */
  checked: number;
  arrivedChecked: number;
  arrivedFar: number;
  completedChecked: number;
  completedFar: number;
  /** Stops where either milestone was beyond the threshold. */
  flagged: number;
  pctFlagged: number | null; // 0..1 of checked
  medianArrivedM: number | null;
  medianCompletedM: number | null;
  p90CompletedM: number | null;
}

export interface MilestoneRegionRow {
  region: string;
  checked: number;
  flagged: number;
  pctFlagged: number;
  medianCompletedM: number | null;
}

export interface MilestoneDriverRow {
  driver: string;
  checked: number;
  flagged: number;
  pctFlagged: number;
  medianCompletedM: number | null;
  maxM: number | null;
}

export interface MilestoneFlaggedRow {
  date: string | null;
  stopRef: string | null;
  region: string | null;
  driver: string | null;
  address: string | null;
  completedTime: string | null;
  arrivedM: number | null;
  completedM: number | null;
}

export interface MilestoneDistanceReport {
  from: string | null;
  to: string | null;
  thresholdM: number;
  totals: MilestoneTotals;
  /** Distribution of Completed-milestone distance from the delivery point. */
  buckets: Array<{ bucket: string; stops: number }>;
  byRegion: MilestoneRegionRow[];
  byDriver: MilestoneDriverRow[];
  flaggedList: MilestoneFlaggedRow[];
}

export interface Filters {
  from: string;
  to: string;
  region: string; // '' = all
  store: string; // '' = all (stringified id)
  driver: string; // '' = all
}

export function filtersToQuery(f: Filters): string {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.region) p.set('region', f.region);
  if (f.store) p.set('store', f.store);
  if (f.driver) p.set('driver', f.driver);
  return p.toString();
}

// ─── Date helpers ────────────────────────────────────────────────────────────

/** Add `days` to an ISO yyyy-mm-dd date, returning the same format (UTC-safe). */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const niceDate = (iso: string | null) =>
  iso
    ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
      })
    : '—';

export const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });

/** 'YYYY-MM' → 'Jun 2026'. */
export const monthLabel = (ym: string) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

// ─── Formatting ──────────────────────────────────────────────────────────────

export const pct = (v: number | null, digits = 1) => (v == null ? 'n/a' : `${(v * 100).toFixed(digits)}%`);
export const num = (v: number) => new Intl.NumberFormat('en-GB').format(Math.round(v));

/** Minutes → "5.4 min" (or "1 h 05 min" once past an hour). */
export const mins = (v: number | null) => {
  if (v == null) return 'n/a';
  if (v < 60) return `${v.toFixed(1)} min`;
  const h = Math.floor(v / 60);
  const m = Math.round(v % 60);
  return `${h} h ${String(m).padStart(2, '0')} min`;
};

/** Kilometres → "12.3 km". */
export const km = (v: number | null) => (v == null ? 'n/a' : `${v.toFixed(1)} km`);

/** Metres → "85 m" / "1.2 km". */
export const metres = (v: number | null) => {
  if (v == null) return 'n/a';
  if (v < 1000) return `${Math.round(v)} m`;
  return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)} km`;
};

/** 0..23 → "9am", "12pm", "5pm" (Europe/London bucket). */
export const hourLabel = (h: number) => {
  const period = h < 12 ? 'am' : 'pm';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${period}`;
};

export interface Delta {
  /** Formatted magnitude, e.g. "+3.2 pts" or "+12%". */
  label: string;
  tone: 'good' | 'bad' | 'neutral';
}

/**
 * Period-over-period delta for a KPI. `higherIsBetter=false` flips the tone
 * (e.g. failures, dwell time). Rate metrics (0..1) are shown in percentage
 * points; everything else as a relative % change. Returns null when there's no
 * comparable previous value.
 */
export function periodDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  opts: { kind: 'rate' | 'count'; higherIsBetter?: boolean } = { kind: 'count' },
): Delta | null {
  if (current == null || previous == null) return null;
  const higherIsBetter = opts.higherIsBetter ?? true;
  let diff: number;
  let label: string;
  if (opts.kind === 'rate') {
    diff = current - previous;
    const pts = diff * 100;
    label = `${pts >= 0 ? '+' : ''}${pts.toFixed(1)} pts`;
  } else {
    diff = current - previous;
    if (previous === 0) {
      label = current === 0 ? 'no change' : 'new';
    } else {
      const rel = (diff / previous) * 100;
      label = `${rel >= 0 ? '+' : ''}${rel.toFixed(0)}%`;
    }
  }
  const tone: Delta['tone'] =
    Math.abs(diff) < 1e-9 ? 'neutral' : diff > 0 === higherIsBetter ? 'good' : 'bad';
  return { label, tone };
}

// ─── CSV export ──────────────────────────────────────────────────────────────

export function toCsv(rows: Array<Record<string, string | number>>): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
}

export function downloadCsv(filename: string, rows: Array<Record<string, string | number>>): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
